import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { SurveyDialogTemplateEditData, SurveyTemplate, SurveyTemplateListResponse } from 'src/app/models/surveyTemplate.model';
import { SurveyTemplateService } from 'src/app/services/surveyTemplate.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services/auth.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { TranslateService } from 'src/app/services/translate.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { sampleRouteConfig } from 'src/app/shared/constants/sampleRouteConfig.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { buildTemplateNameCell } from 'src/app/shared/helpers/template-name-cell.helper';
import { TemplateRowActions } from 'src/app/shared/helpers/template-row-actions.helper';
import { SurveyDialogComponent } from 'src/app/modals/survey-dialog/survey-dialog.component';
import { TemplateKindToggleComponent } from 'src/app/components/template-kind-toggle/template-kind-toggle.component';

@Component({
  standalone: false,
  selector: 'app-survey-template-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './survey-template-list.component.scss']
})
export class SurveyTemplateListComponent extends CoreGridComponent<SurveyTemplateListResponse> {
  private readonly destroy$ = new Subject<void>();
  private readonly rowActions: TemplateRowActions<SurveyTemplateListResponse, SurveyTemplate>;

  constructor(
    protected surveyTemplateService: SurveyTemplateService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private translateService: TranslateService,
    private organizationLevelService: OrganizationLevelService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = surveyTemplateService;
    this.deleteDisabled = true;
    this.editDisabled = true;
    this.canCreate = false;
    this.rowActions = new TemplateRowActions<SurveyTemplateListResponse, SurveyTemplate>({
      service: surveyTemplateService,
      dialog,
      toastService,
      translate: key => this.translateService.getDynamicTranslation(key),
      destroy$: this.destroy$,
      reload: () => this.loadPage(),
      deleteTitle: 'Survey Template',
      edit: row => this.openEditDialog(row),
      setDefault: row => surveyTemplateService.setDefault({ defaultSurveyTemplateId: row.id }),
      clearDefault: () => surveyTemplateService.clearDefault()
    });
    this.rowActionMenu = (row) => this.rowActions.buildMenuItems(row);
    this.toolbarComponent = TemplateKindToggleComponent;
  }

  protected get objectId(): string {
    return RolePermissionObjects.SurveyTemplate;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Survey Template';
    this.searchPlaceholder = 'Search Templates';
    this.sortActive = nameOf<SurveyTemplateListResponse>(x => x.name);
    this.defaultOrderBy = 'asc';
    this.rowActionMenuPanelClass = 'row-action-menu--compact';
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<SurveyTemplateListResponse>({
        name: nameOf<SurveyTemplateListResponse>(x => x.name),
        displayName: 'Name',
        cellTemplate: (row) => buildTemplateNameCell(row, key => this.translateService.getDynamicTranslation(key))
      }),
      new ColumnConfig<SurveyTemplateListResponse>({
        name: nameOf<SurveyTemplateListResponse>(x => x.createdByUserName),
        displayName: 'Created By',
        cellTemplate: (row) => escapeHtml(row.createdByUserName) || '-'
      }),
      new ColumnConfig<SurveyTemplateListResponse>({
        name: nameOf<SurveyTemplateListResponse>(x => x.organizationLevelId),
        displayName: 'Visibility',
        isSortable: false,
        cellTemplate: (row) => row.isShared
          ? (escapeHtml(row.organizationLevelName) || '-')
          : this.translateService.getDynamicTranslation('Private')
      }),
      new ColumnConfig<SurveyTemplateListResponse>({
        name: 'action',
        displayName: 'Actions',
        isSortable: false,
        isDisplayNameConfigurable: false
      })
    ];
  }

  protected override objectFilterMap(propertyName: string): OrganizationLevelService | undefined {
    if (propertyName === nameOf<SurveyTemplateListResponse>(x => x.organizationLevelId)) {
      return this.organizationLevelService;
    }
    return undefined;
  }

  private openEditDialog(row: SurveyTemplateListResponse): void {
    const payload: SurveyDialogTemplateEditData = { editSurveyTemplateId: row.id };
    const dialogRef = this.dialog.open(SurveyDialogComponent, {
      data: payload,
      width: '920px',
      disableClose: true
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe({
      next: (saved: boolean) => {
        if (saved) this.loadPage();
      }
    });
  }

  override onRowClicked(row: SurveyTemplateListResponse): void {
    if (this.isDialog) {
      super.onRowClicked(row);
      return;
    }
    if (row.canManage) {
      this.openEditDialog(row);
    }
  }

  override initializeTabs() {
    return sampleRouteConfig;
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(_entityId?: string): void {}

  override openEntityDeleteDialog(entity: SurveyTemplateListResponse): void {
    this.rowActions.openDeleteDialog(entity);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }
}
